import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  makeStyles,
  MuiThemeProvider,
  createMuiTheme,
} from "@material-ui/core";
import { useParams, useHistory } from "react-router-dom";
import MUIDataTable from "mui-datatables";
import Skeleton from "@material-ui/lab/Skeleton";
import NavigationTabs from "../components/NavigationTabs";
// API services
import { listPointsLog } from "../api/Points";
const { forwardRef } = React;

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    [theme.breakpoints.up("lg")]: {
      paddingLeft: "190px",
    },
  },
  exportButtonProgress: {
    position: "absolute",
    marginTop: 5,
    marginLeft: 5,
  },
}));

// Date formatting function MM/DD/YYYY
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const PointsLogList = forwardRef(() => {
  const classes = useStyles();
  const history = useHistory();
  let { client_id } = useParams();
  const [columnViewPorcentage, setColumViewPorcentage] = useState(100 / 6);
  const [clientUsers, setClentUsers] = useState([]);
  const [fromClient] = React.useState(client_id ? true : false);
  const [loading, setLoading] = useState(false);

  // List users at the first time and when client_id changes
  useEffect(() => {
    getPointsLog();
  }, [client_id]);

  // Get user entries
  const getPointsLog = () => {
    setLoading(true);
    const filterClientId = fromClient && client_id ? client_id : null;
    // Get all users for table view
    listPointsLog(filterClientId)
      .then((result) => {
        if (result && result.data && result.data.queueWorkerLogs) {
          const data = result.data.queueWorkerLogs;
          const listLog = [];
          for (const point of data) {
            const fullName = point.created_by_user?.Details
              ? point.created_by_user.Details.FirstName +
                " " +
                point.created_by_user.Details.LastName
              : null;
            const item = {
              index: point.id,
              id: point.id,
              assigned_by: fullName || point.created_by_user?.username || "-",
              points: point.JsonData.Points
                ? JSON.stringify(point.JsonData.Points)
                : "-",
              division: point.JsonData.DivisionCategoryId
                ? point.JsonData.DivisionCategoryName
                : "ALL",
              date: formatDate(point.created_at),
              repeat: point.RepeatType ? point.RepeatType : "No repetition",
              action: point.Type == "AddPoints" ? "Add" : "Reset",
              pointsIds: point.JsonData.points,
              note: point.JsonData.Note
            };
            listLog.push(item);
          }
          setClentUsers(listLog);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => setLoading(false)); // set loading to false
  };

  // Mui Data table custom Styles
  const theme = () =>
    createMuiTheme({
      overrides: {
        //Table head custom styles
        MUIDataTableHeadCell: {
          fixedHeader: {
            paddingTop: "0px",
            paddingBottom: "10px",
            backgroundColor: "#F2F2F2",
            borderRight: "1px solid #E5E3E3",
          },
        },
        // Search form custom style
        MUIDataTableSearch: {
          main: {
            visibility: true,
          },
        },
        // Chip filters custom styles
        MUIDataTableFilterList: {
          root: {
            marginBottom: "10px",
          },
        },
        // Filter container custom styles
        MuiGrid: {
          container: {
            display: "block",
            width: "210px",
            paddingLeft: "opx",
          },
          item: {
            boxSizing: "content-box",
          },
        },
        // Filter list options custom styles
        MUIDataTableFilter: {
          checkboxIcon: {
            padding: "0px",
          },
          checkboxFormControlLabel: {
            padding: "0px",
          },
          checkboxListTitle: {
            width: "100%",
          },
        },
        MUIDataTableSelectCell: {
          headerCell: {
            backgroundColor: "#F2F2F2",
          },
        },
        MUIDataTableToolbarSelect: {
          root: { visibility: "hidden", minHeight: "49px" },
        },
        MuiTableHead: {
          root: {
            display: `${columnViewPorcentage ? "table-header-group" : "none"}`,
          },
        },
        MuiTableBody: {
          root: {
            display: `${columnViewPorcentage ? "table-row-group" : "none"}`,
          },
        },
      },
    });

  // Custom styles for text column
  const customStyle = ({ ...column }) => {
    return (
      <div
        className={classes.ListItem}
        style={{
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          overflow: "hidden",
          fontWeight: "bold",
          padding: "10px",
          height: "10px",
          width: "100%",
        }}
      >
        {column.label}
      </div>
    );
  };

  // Custom style by columns
  const customStyleText = (value = "") => {
    // Validate user id
    let userId = null;
    if (value && value.startsWith('{"id":')) {
      const user = JSON.parse(value);
      // Get user name
      value = user.username || "-";
      userId = user.id || null;
    }
    value = value && value.length !== 0 ? value : "-";
    // Adding user id
    const key = userId ? { key: userId } : {};
    return (
      <div
        {...key}
        id={userId ? userId : `user-row-${new Date().toLocaleString()}`}
        style={{
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          overflow: "hidden",
        }}
      >
        {value}
      </div>
    );
  };

  const initialColums = [
    // Assigned by
    {
      name: "assigned_by",
      label: "ASSIGNED BY",
      options: {
        filter: false,
        setCellProps: () => {
          return {
            style: {
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            },
          };
        },
        customHeadLabelRender: customStyle,
        customBodyRender: (value) => customStyleText(value),
      },
    },
    // Points
    {
      name: "points",
      label: "POINTS",
      options: {
        filter: true,
        setCellProps: () => {
          return {
            style: {
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            },
          };
        },
        customHeadLabelRender: customStyle,
        customBodyRender: (value) => customStyleText(value),
      },
    },
    // Division
    {
      name: "division",
      label: "DIVISION",
      options: {
        filter: true,
        setCellProps: () => {
          return {
            style: {
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            },
          };
        },
        customHeadLabelRender: customStyle,
        customBodyRender: (value) => customStyleText(value),
      },
    },
    // Date
    {
      name: "date",
      label: "DATE",
      options: {
        filter: true,
        setCellProps: () => {
          return {
            style: {
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            },
          };
        },
        customHeadLabelRender: customStyle,
        customBodyRender: (value) => customStyleText(value),
      },
    },
    // Repeat
    {
      name: "repeat",
      label: "REPEAT",
      options: {
        filter: false,
        setCellProps: () => {
          return {
            style: {
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            },
          };
        },
        customHeadLabelRender: customStyle,
        customBodyRender: (value) => customStyleText(value),
      },
    },
    // Action
    {
      name: "action",
      label: "ACTION",
      options: {
        filter: false,
        setCellProps: () => {
          return {
            style: {
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            },
          };
        },
        customHeadLabelRender: customStyle,
        customBodyRender: (value) => customStyleText(value),
      },
    },
  ];

  // Table options
  const options = {
    filter: true,
    filterType: "checkbox",
    sortFilterList: true,
    selectableRows: "none",
    searchPlaceholder: "Search all users",
    textLabels: {
      body: {
        noMatch: "Sorry, no matching records found",
        toolTip: "Sort",
        columnHeaderTooltip: (column) => `${column.label}`,
      },
    },
    download: false,
    responsive: "standard",
    onRowClick: (rowData, rowMeta) => {
      const point = clientUsers[rowMeta.dataIndex];
      if (point && point.id) {
        const url = `/app/client/${client_id}/points_log/${point.id}`;
        localStorage.setItem("pointLog", JSON.stringify(point));
        history.push(url);
      }
    },
    elevation: 0,
    onTableChange: (action, state) => {
      if (action == "filterChange") {
        const usersData =
          state && state.displayData && state.displayData.length > 0
            ? state.displayData
            : [];
        let filterUsers = [];
        for (const userData of usersData) {
          const username = userData.data[1].key;
          const user = clientUsers.find((user) => user.id == username);
          if (user && typeof user == "object") {
            filterUsers.push(user.id);
          }
        }
      }
      if (action == "viewColumnsChange") {
        const columns = state.columns;
        let countColumns = columns.length;
        let showColumns = 0;
        for (const column of columns) {
          if (column.display == "true") {
            showColumns++;
          }
        }
        if (showColumns !== columns.length) {
          countColumns = showColumns;
        }
        const columnViewPorcentage =
          countColumns > 0 ? 100 / countColumns : null;
        setColumViewPorcentage(columnViewPorcentage);
      }
    },
    pagination: true,
    rowsPerPage: 10,
    rowsPerPageOptions: [10, 20, 50, 100],
  };

  /**
   * Function to handle right click on table row
   * @param {MouseEvent} event - The mouse event object
   */
  const handleContextMenu = (event) => {
    // Find the closest table row
    if (!event || !event.target || typeof event.target.closest !== "function")
      return;
    const tr = event.target.closest("tr");
    if (!tr) return; // If no row found, exit the function
    // Initialize tr
    let index = fromClient ? 2 : 1; // Default index for non-client view
    // Get the third column (index 2) of the row
    const cell = tr.querySelectorAll("td")[index];
    if (!cell) return; // If no cell found, exit the function
    // Get the user id from the cell's div
    const userId = cell
      .querySelector("div")
      .querySelector("div")
      .getAttribute("id");
    // Check if the user id is valid
    if (!userId) {
      return;
    }
    // Prevent the default context menu from appearing
    event.preventDefault();
  };

  return (
    <Container
      style={{ marginTop: "65px" }}
      className={classes.mainContainer}
      maxWidth="xl"
    >
      <Grid container spacing={3} style={{ padding: "20px" }} id="second_grid">
        {/* NavigationTabs */}
        <Grid item xs={12} md={10} style={{ paddingBottom: "20px" }}>
          <NavigationTabs client_id={Number(client_id)} value={"points_log"} />
        </Grid>
        {/*Skeleton*/}
        {loading ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={9}>
                  <Skeleton
                    variant="rectangular"
                    height={30}
                    style={{ margin: "3px" }}
                    width={120}
                  />
                </Grid>
                <Grid
                  item
                  xs={3}
                  style={{ paddingRight: "25px", marginBottom: "20px" }}
                >
                  <Skeleton
                    variant="rectangular"
                    height={30}
                    style={{ margin: "3px", float: "right" }}
                    width={40}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={30}
                    style={{ margin: "3px", float: "right" }}
                    width={40}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={30}
                    style={{ margin: "3px", float: "right" }}
                    width={40}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={30}
                    style={{ margin: "3px", float: "right" }}
                    width={40}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={30}
                    style={{ margin: "3px", float: "right" }}
                    width={40}
                  />
                </Grid>
              </Grid>
              <Grid container>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((row, i) => {
                  return (
                    <Grid key={i} container>
                      <Grid item xs={1}>
                        <Skeleton
                          variant="rectangular"
                          height={30}
                          style={{ margin: "3px", marginBottom: "15px" }}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Skeleton
                          variant="rectangular"
                          height={30}
                          style={{ margin: "3px" }}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Skeleton
                          variant="rectangular"
                          height={30}
                          style={{ margin: "3px" }}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <Skeleton
                          variant="rectangular"
                          height={30}
                          style={{ margin: "3px" }}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Skeleton
                          variant="rectangular"
                          height={30}
                          style={{ margin: "3px" }}
                        />
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <Grid xs={12} sm={12} md={12} lg={12}>
            {/**Client Users Table List */}
            <MuiThemeProvider theme={theme}>
              <div
                onContextMenu={handleContextMenu}
                onClick={handleContextMenu}
              >
                <MUIDataTable
                  data={clientUsers}
                  columns={initialColums}
                  options={options}
                />
              </div>
            </MuiThemeProvider>
          </Grid>
        )}
      </Grid>
    </Container>
  );
});

PointsLogList.displayName = "PointsLogList";

export default PointsLogList;
